import type { CSSProperties } from 'react'
import type { AreaChartPoint } from '../AreaChart/AreaChart.types'

export type { AreaChartPoint as LineChartPoint }

export type LineChartSeries = {
  /** Legend / tooltip label. */
  name: string
  /** Points for this series (same category order across series). */
  data: AreaChartPoint[]
  /** Stroke color. Falls back to palette by index. */
  color?: string
  /** Override stroke width for this series. */
  strokeWidth?: number
}

export const LINE_CHART_DEFAULT_WIDTH = 680
export const LINE_CHART_DEFAULT_HEIGHT = 220
export const LINE_CHART_DEFAULT_STROKE_WIDTH = 2
export const LINE_CHART_STROKE_COLOR = 'var(--ds-blue-500)'

/** Default palette for multi-series line charts. */
export const LINE_CHART_SERIES_COLORS = [
  'var(--ds-blue-500)',
  'var(--ds-cyan-400)',
  'var(--ds-indigo-500)',
] as const

export const LINE_CHART_DEFAULT_DATA: AreaChartPoint[] = [
  { label: 'Mon', value: 42 },
  { label: 'Tue', value: 58 },
  { label: 'Wed', value: 51 },
  { label: 'Thu', value: 73 },
  { label: 'Fri', value: 66 },
  { label: 'Sat', value: 38 },
  { label: 'Sun', value: 29 },
]

/** Default multi-series sample (3 lines) for gallery / Dashboard-style previews. */
export const LINE_CHART_DEFAULT_SERIES: LineChartSeries[] = [
  {
    name: 'Visits',
    color: LINE_CHART_SERIES_COLORS[0],
    data: [
      { label: 'Mon', value: 42 },
      { label: 'Tue', value: 58 },
      { label: 'Wed', value: 51 },
      { label: 'Thu', value: 73 },
      { label: 'Fri', value: 66 },
      { label: 'Sat', value: 38 },
      { label: 'Sun', value: 29 },
    ],
  },
  {
    name: 'New patients',
    color: LINE_CHART_SERIES_COLORS[1],
    data: [
      { label: 'Mon', value: 18 },
      { label: 'Tue', value: 24 },
      { label: 'Wed', value: 21 },
      { label: 'Thu', value: 32 },
      { label: 'Fri', value: 28 },
      { label: 'Sat', value: 14 },
      { label: 'Sun', value: 11 },
    ],
  },
  {
    name: 'Recalls',
    color: LINE_CHART_SERIES_COLORS[2],
    data: [
      { label: 'Mon', value: 30 },
      { label: 'Tue', value: 36 },
      { label: 'Wed', value: 41 },
      { label: 'Thu', value: 45 },
      { label: 'Fri', value: 39 },
      { label: 'Sat', value: 22 },
      { label: 'Sun', value: 17 },
    ],
  },
]

export type LineChartProps = {
  /**
   * Single series values. Used when `series` is omitted.
   * Defaults to the Figma demo path.
   */
  data?: AreaChartPoint[]
  /**
   * Multiple stroke series. When set, takes precedence over `data`.
   */
  series?: LineChartSeries[]
  width?: number
  height?: number
  /** Stroke color for the single-series (`data`) mode. */
  strokeColor?: string
  strokeWidth?: number
  maxValue?: number
  minValue?: number
  showTooltip?: boolean
  /** Series name for single-series (`data`) mode. Defaults to "Value". */
  seriesName?: string
  formatValue?: (value: number) => string
  darkMode?: boolean
  className?: string
  style?: CSSProperties
  'aria-label'?: string
}
